import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import PencilIcon from '@/assets/icons/pencil.svg?react'
import { Loading, Pagination, Trans } from '@/components'

import { fetchRouteModels } from '../api'
import { ModelRoute } from '../types'
import { CapabilityTag } from './CapabilityTag'
import { ShowingRange } from './ShowingRange'

const PAGE_SIZE = 5

interface RouteModelListProps {
  route: ModelRoute
}

export function RouteModelList({ route }: RouteModelListProps) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['routeModels', route.resID, page],
    queryFn: () => fetchRouteModels(route.resID, page, PAGE_SIZE),
  })

  if (isLoading || !data) {
    return (
      <div className="bg-panel-gray-100 rounded-lg flex items-center justify-center p-8">
        <Loading />
      </div>
    )
  }

  return (
    <div className="bg-panel-gray-100 rounded-lg p-4 t-sm text-tertiary">
      <div className="grid grid-cols-12 gap-4 px-4 pb-2 t-xs text-secondary">
        <div className="col-span-2">
          <Trans ns="modelRoute" i18nKey="detail.modelList.headerProvider">
            Provider
          </Trans>
        </div>
        <div className="col-span-2">
          <Trans ns="modelRoute" i18nKey="detail.modelList.headerModel">
            Model
          </Trans>
        </div>
        <div className="col-span-5">
          <Trans ns="modelRoute" i18nKey="detail.modelList.headerCapabilities">
            Capabilities
          </Trans>
        </div>
        <div className="col-span-3">
          <Trans ns="modelRoute" i18nKey="detail.modelList.headerPrice">
            Input /Output Price - 1M
          </Trans>
        </div>
      </div>

      <div className="bg-panel-white rounded-lg divide-y divide-neutral-100">
        {data.items.map((item, index) => (
          <div key={index} className="grid grid-cols-12 gap-4 items-center px-4 py-3">
            <div className="col-span-2 t-sb text-primary">{item.provider}</div>
            <div className="col-span-2 t-sb text-primary">{item.model}</div>
            <div className="col-span-5 flex flex-wrap gap-2">
              {item.capabilities.map((capability) => (
                <CapabilityTag key={capability}>{capability}</CapabilityTag>
              ))}
            </div>
            <div className="col-span-3">
              <span className="t-sb text-primary">
                {item.inputPrice} / {item.outputPrice}
              </span>{' '}
              <span className="text-secondary">
                <Trans ns="modelRoute" i18nKey="table.credits">
                  Credits
                </Trans>
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-3 px-4">
        <ShowingRange current={page} size={PAGE_SIZE} total={data.total} />
        <Pagination size={PAGE_SIZE} total={data.total} current={page} onChange={setPage} />
      </div>

      <div className="border-t border-neutral-200 mt-3 pt-3 px-4">
        <button
          type="button"
          className="flex items-center gap-2 t-sb text-highlight cursor-pointer hover:opacity-80"
          onClick={() => navigate(`/model-route/${route.resID}/edit`)}
        >
          <PencilIcon className="w-4 h-4" />
          <Trans ns="modelRoute" i18nKey="detail.modelList.manage">
            Manage
          </Trans>
        </button>
      </div>
    </div>
  )
}
